import { useEffect, useMemo, useState } from 'react';
import { CreditPlanStatus } from '@/@types/client-api';
import RoundedButton, { RoundedButtonProps } from '@/components/ui/Buttons/RoundedButton';
import { useCreditPlanContext } from '@/contexts/creditPlan';
import { requestStart } from '@/redux/slices/signalRSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { ScreenSizeQueries } from '@/constants/breakpoints';
import ComplexTooltip from '@/components/ui/Tooltip/ComplexTooltip';
import { existsCookie, setCookieValues } from '@/utils/cookie-list-values';
import { RootState } from '@/redux/store';
import { TooltipBlock } from '@/@types/content';
import RichText from '@/components/cms/RichText';
import { selectHeaderIsInView } from '@/redux/selectors/pageSelectors';
import classname from 'classnames';
import { selectCreditPlansFetchStatus } from '@/redux/selectors/creditPlanSelectors';
import { AnalyticsContext, AnalyticsEventName } from '@/analytics/constants';
import { useAnalytics } from '@/analytics/hooks/useAnalytics';
import { shouldShowCreditPlanButtonLabel } from './CreditPlanButton.helpters';

const TOOLTIP_COOKIE_NAME = 'pli_credit_plan_tooltip_dismissed';
const STANDALONE_LABEL_BREAKPOINT_QUERY = '(min-width: 1222px)';

type CreditPlanButtonProps = {
  className?: string;
  hasCartButton?: boolean;
};

const CreditPlanButton = ({ className, hasCartButton = false }: CreditPlanButtonProps) => {
  const { openPanel: openCreditPlanPanel, isPanelOpen, creditPlans } = useCreditPlanContext();
  const headerIsInView = useSelector(selectHeaderIsInView);
  const creditPlansFetchStatus = useSelector(selectCreditPlansFetchStatus);

  const blockContent = useSelector((state: RootState) => state.creditPlan.content);
  const tooltipContent = blockContent?.headerTooltip?.[0] as TooltipBlock | undefined;

  const isAtOrAboveStandaloneLabelBreakpoint = useMediaQuery(STANDALONE_LABEL_BREAKPOINT_QUERY);
  const isXlAndAbove = useMediaQuery(ScreenSizeQueries.xl);

  const dispatch = useDispatch();

  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const { trackEvent } = useAnalytics();

  const creditPlansLoaded = useMemo(
    () => creditPlansFetchStatus === 'completed',
    [creditPlansFetchStatus]
  );

  const validCreditPlans = useMemo(() => {
    const validStatuses = [
      CreditPlanStatus.STARTED,
      CreditPlanStatus.EXECUTING,
      CreditPlanStatus.COMPLETED,
      CreditPlanStatus.FAILED
    ];
    return creditPlans.filter(plan => plan.status && validStatuses.includes(plan.status));
  }, [creditPlans]);

  const hasCompletedPlans = useMemo(
    () => validCreditPlans.some(plan => plan.status === CreditPlanStatus.COMPLETED),
    [validCreditPlans]
  );

  useEffect(() => {
    if (
      creditPlans.some(plan =>
        [CreditPlanStatus.STARTED, CreditPlanStatus.EXECUTING].includes(plan.status!)
      )
    ) {
      dispatch(requestStart());
    }
  }, [creditPlans, dispatch]);

  useEffect(() => {
    const hasTooltipBeenDismissed = existsCookie(TOOLTIP_COOKIE_NAME);
    setIsTooltipOpen(hasCompletedPlans && !hasTooltipBeenDismissed && headerIsInView);
  }, [hasCompletedPlans, headerIsInView, validCreditPlans]);

  const handleTooltipClose = () => {
    setIsTooltipOpen(false);
    setCookieValues(TOOLTIP_COOKIE_NAME, ['true'], 1, 365);
  };

  const handleCreditPlanClick = useMemo(
    () => () => {
      if (validCreditPlans.length === 0) return;

      trackEvent(AnalyticsEventName.SelectViewCreditplan, {
        context: AnalyticsContext.Navigation
      });

      if (validCreditPlans.length === 1) {
        openCreditPlanPanel(validCreditPlans[0].jurisdiction!);
      } else {
        openCreditPlanPanel();
      }
    },
    [validCreditPlans, trackEvent, openCreditPlanPanel]
  );

  const showLabel = useMemo(
    () =>
      shouldShowCreditPlanButtonLabel({
        hasCartButton,
        isAtOrAboveStandaloneLabelBreakpoint,
        isXlAndAbove
      }),
    [hasCartButton, isAtOrAboveStandaloneLabelBreakpoint, isXlAndAbove]
  );

  const creditPlanButtonProps = useMemo(() => {
    if (validCreditPlans.length === 0) return undefined;

    const hasAnyComplete = validCreditPlans.some(
      plan => plan.status === CreditPlanStatus.COMPLETED
    );

    const buttonColorProps = hasAnyComplete
      ? { icon: 'ai-tool', color: 'gradient' as RoundedButtonProps['color'] }
      : { icon: 'ai-tool-gradient', color: 'gray' as RoundedButtonProps['color'] };

    const buttonProps = {
      ...buttonColorProps,
      label: showLabel ? 'Credit plan' : undefined,
      ['aria-label']: 'Credit plan',
      buttonSize: 'large' as RoundedButtonProps['buttonSize'],
      onClick: handleCreditPlanClick,
      className,
      ['data-component']: 'CreditPlanButton'
    };

    return buttonProps;
  }, [className, handleCreditPlanClick, showLabel, validCreditPlans]);

  if (!creditPlanButtonProps) return <></>;

  return (
    !!creditPlansLoaded && (
      <div>
        <RoundedButton {...creditPlanButtonProps} />
        <ComplexTooltip
          isOpen={isTooltipOpen}
          onOpenChange={setIsTooltipOpen}
          variant="dark"
          modal={false}
        >
          <ComplexTooltip.Trigger asChild>
            <span className="relative left-[54%] top-2" />
          </ComplexTooltip.Trigger>
          <ComplexTooltip.Content
            side="bottom"
            align="center"
            onClose={handleTooltipClose}
            closeLabel="Close credit plan tooltip"
            className={classname({
              'animate-fadeIn': headerIsInView,
              'animate-fadeOut': !headerIsInView
            })}
            nestedInModal={
              !isPanelOpen
            } /* NestedInModal is needed to prevent tooltip from being obscured by Header nav Search input, but is visually undesirable when the panel is open */
            onPointerDownOutside={(e: Event) => e.preventDefault()}
          >
            <div className="text-2 flex flex-col gap-1">
              <p className="font-semibold">{tooltipContent?.title}</p>
              <RichText content={tooltipContent?.copy} className="" />
            </div>
          </ComplexTooltip.Content>
        </ComplexTooltip>
      </div>
    )
  );
};

export default CreditPlanButton;
